import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../utils/hooks";
import { logout, checkIsSubscribed } from "../utils/functions";
import Layout from "./Layout";
import { role } from "../utils/data";

interface Props {
    children: React.ReactNode;
    requireSubscription?: boolean;
    allowedRoles?: string[];
    disallowedRoles?: string[];
}

const AuthRoute: React.FC<Props> = ({ 
    children, 
    requireSubscription = false,
    allowedRoles,
    disallowedRoles
}) => {
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const location = useLocation();

    useAuth({
        errorCallBack: () => {
            setIsAuthenticated(false);
            setLoading(false);
            logout();
        },
        successCallBack: () => {
            setIsAuthenticated(true);
            setLoading(false);
        }
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1E3A8A]"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // 1. Role-Based Access Control (RBAC)
    const userRole = (localStorage.getItem(role) || 'user').toLowerCase();

    // Check disallowed roles (e.g. blocking Admin from user-only support routes)
    if (disallowedRoles && disallowedRoles.length > 0) {
        const isDisallowed = disallowedRoles.some(r => r.toLowerCase() === userRole);
        if (isDisallowed) {
            return <Navigate to="/" replace />;
        }
    }

    // Check allowed roles (e.g. restricting to Admin only)
    if (allowedRoles && allowedRoles.length > 0) {
        const hasRequiredRole = allowedRoles.some(r => r.toLowerCase() === userRole);
        if (!hasRequiredRole) {
            return <Navigate to="/" replace />;
        }
    }

    // 2. Subscription-Based Gating
    if (requireSubscription) {
        const searchParams = new URLSearchParams(location.search);
        const hasViewFlag = searchParams.get('view') === 'true';
        const hasCaseIdentifier = Boolean(
            searchParams.get('id') || 
            searchParams.get('case_no') || 
            searchParams.get('case_id') ||
            searchParams.get('caseNumber')
        );
        
        // Only allow review mode if accompanied by a specific case ID
        const isViewingExistingCase = hasViewFlag && hasCaseIdentifier;
        
        const isAdmin = userRole === 'admin';
        const isSubscribed = checkIsSubscribed(null, null, isAdmin);

        if (!isSubscribed && !isViewingExistingCase) {
            return <Navigate to="/settings/billing" replace state={{ from: location }} />;
        }
    }

    return <Layout>{children}</Layout>;
};

export default AuthRoute;